import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { Link } from '@hologram-dimension/link';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { Popover } from '@hologram-dimension/popover';
import { useSimDetails, useSimDeviceDetails, useSimProfiles, useSimStatuses } from 'api';
import useOpenSessions from 'api/legacy/session/useOpenSessions';
import clsx from 'clsx/lite';
import { ApnInfoIcon, EmptyState, Field } from 'components';
import useUserInfo from 'hooks/User/useUserInfo';
import { type FC, type ReactNode, useMemo } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type {
  EnumsSimNetworkState,
  ProfilesDaoSimProfileDetails,
} from 'types/__generated__/SimInventoryApi';
import { formatDateTime } from 'utils/dates';
import { useStatusMap } from 'utils/hooks';
import useSimUrl from 'utils/hooks/useSimUrl';
import EnumSimPage from 'utils/legacy/paths/simPage';
import styles from './SimProfiles.module.css';

interface SimProfileProps {
  simProfile: ProfilesDaoSimProfileDetails;
  fallback: boolean;
  fallbackProtected: boolean;
  connected?: boolean;
  deactivated?: boolean;
  renderApnBanner?: boolean;
  msisdn?: string;
  simDetailBaseUrl: string;
  hologramSimId?: HologramSimId;
  simHasMultipleProfiles?: boolean;
}

const installationStateToFriendly = new Map([
  ['ENABLED', 'Enabled'],
  ['DISABLED', 'Disabled'],
  ['ENABLE_PENDING', 'Disabled, pending switch to enabled'],
  ['DISABLE_PENDING', 'Enabled, pending switch to disabled'],
  ['DOWNLOADED', 'Downloaded'],
]);

const RenderSimProfile: FC<SimProfileProps> = ({
  simProfile,
  fallback,
  fallbackProtected,
  connected = false,
  deactivated = false,
  renderApnBanner,
  msisdn,
  simDetailBaseUrl,
  hologramSimId,
  simHasMultipleProfiles = false,
}) => {
  const {
    iccid,
    installationState,
    networkState,
    simProfileId,
    whenCreated,
    requiredApn,
    profileName,
    profileType,
  } = simProfile;

  const { statusInfo } = useStatusMap(networkState);
  const isActiveProfile = simProfile?.installationState === 'ENABLED';
  let installationStateFriendly: ReactNode = <EmptyState />;

  if (installationState) {
    if (installationStateToFriendly.has(installationState)) {
      installationStateFriendly = installationStateToFriendly.get(installationState);
    } else {
      installationStateFriendly = installationState;
    }
  }

  const { profileIcon, profileTitle, profileDescription } = useMemo(() => {
    let icon: ReactNode = null;
    let title: string = '';
    let description: string = '';
    if (isActiveProfile) {
      description = 'Your SIM will connect to this profile when using data.';
      if (connected) {
        title = 'Connected using the active profile';
        icon = (
          <div className={clsx(styles.connectedIcon, styles.profileIcon)}>
            <Icon name="StatusConnected" size="medium" />
          </div>
        );
      } else {
        title = deactivated ? 'Last active profile' : 'Active profile';
        icon = (
          <div className={clsx(styles.readyIcon, styles.profileIcon)}>
            <Icon name={connected ? 'StatusConnected' : 'StatusReady'} size="medium" />
          </div>
        );
      }
    } else if (fallback && fallbackProtected) {
      title = 'You’re protected if you lose connection';
      description = 'You can manually switch to this profile';
      icon = (
        <div className={clsx(styles.fallbackIcon, styles.profileIcon)}>
          <Icon name="Shield" size="medium" />
        </div>
      );
    } else {
      title = 'This profile is installed but unavailable';
      description =
        'This SIM requires an over-the-air update via our support team to switch profiles.';
      icon = (
        <div className={clsx(styles.unavailableIcon, styles.profileIcon)}>
          <Icon name="Close" size="medium" />
        </div>
      );
    }

    return { profileIcon: icon, profileTitle: title, profileDescription: description };
  }, [fallback, fallbackProtected, isActiveProfile, connected, deactivated]);

  const unavailable = !isActiveProfile && !(fallback && fallbackProtected);

  const orgId = useOrganization();
  const coverageUrl = useSimUrl({
    simDetailBaseUrl,
    orgId,
    simId: hologramSimId,
    subpage: EnumSimPage.coverage,
  });

  return (
    <>
      <div
        className={clsx(
          styles.simProfile,
          isActiveProfile && styles.activeProfile,
          connected && isActiveProfile && styles.connectedProfile,
          deactivated && styles.deactivatedSim
        )}
      >
        <div className={styles.accentBar} />
        <div className={styles.simProfileHeader}>
          {profileIcon}
          <div className={styles.simProfileHeaderText}>
            <span className={clsx(styles.simProfileTitle, unavailable && styles.diminished)}>
              {profileTitle}
            </span>
            <span className={styles.simProfileDescription}>{profileDescription}</span>
          </div>
          <Button
            variant="tertiary"
            to={`${coverageUrl!}${simHasMultipleProfiles ? `?profile=${simProfileId}` : ''}`}
            iconEnd="ArrowEastShort"
          >
            Coverage and rates
          </Button>
        </div>
        <div className={styles.simProfileInfo}>
          <div className={styles.simProfileFields}>
            <Field
              label="Profile name"
              valueClassname={clsx(unavailable && styles.diminished)}
              value={
                <span
                  className={clsx(
                    ((fallback && fallbackProtected) || isActiveProfile) &&
                      styles.highlightedProfileName
                  )}
                >
                  {profileName}
                </span>
              }
            />
            <Field
              label="Required APN"
              valueClassname={clsx(unavailable && styles.diminished)}
              value={requiredApn ? <ApnInfoIcon profiles={[simProfile]} /> : null}
            />
            <Field
              label="Network status"
              valueClassname={clsx(unavailable && styles.diminished)}
              value={statusInfo?.status ?? <EmptyState />}
            />
            <Field
              label="Profile/Link ID"
              valueClassname={clsx(unavailable && styles.diminished, styles.numeric)}
              value={
                <div className={clsx(styles.profileId, styles.numeric)}>
                  <span>{simProfileId}</span>
                  <Popover
                    id="profileId"
                    content="Profile ID is referred to as “Link ID” in API calls."
                    className={clsx(styles.numeric, styles.popover)}
                  >
                    <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
                  </Popover>
                </div>
              }
            />
          </div>
          <div className={styles.simProfileFields}>
            <Field
              label="ICCID"
              valueClassname={clsx(unavailable && styles.diminished, styles.numeric)}
              value={iccid}
            />
            <Field label="Profile status" value={installationStateFriendly} />
            <Field
              label="Created"
              valueClassname={clsx(unavailable && styles.diminished, styles.numeric)}
              value={
                <Popover
                  id="profile-created-time"
                  content={formatDateTime(whenCreated, '12hrlocal')}
                  className={styles.popover}
                >
                  <span>{formatDateTime(whenCreated, '24hrutc')}</span>
                </Popover>
              }
            />
            <Field
              label="MSISDN"
              valueClassname={clsx(unavailable && styles.diminished, styles.numeric)}
              value={msisdn}
            />
          </div>
        </div>
        {profileType === 'BOOTSTRAP' && (
          <div className={styles.simProfileWell}>
            This is the ICCID printed on your SIM card. This bootstrap profile is permanently
            associated with this SIM.
          </div>
        )}
      </div>
      {renderApnBanner && isActiveProfile && (
        <BannerNotification variant="reminder" className={styles.apnBanner}>
          Your SIM card has profiles that require different APNs. The device this SIM is in will
          need to switch APNs if the profile changes.{' '}
          <Link
            href="https://support.hologram.io/hc/en-us/articles/360035697853-How-do-I-set-the-APN-"
            target="_blank"
            type="inline"
          >
            Read about changing APNs.
          </Link>
        </BannerNotification>
      )}
    </>
  );
};

interface SimProfilesProps {
  hologramSimId?: HologramSimId;
  simDetailBaseUrl: string;
}

const SimProfiles: FC<SimProfilesProps> = ({ hologramSimId, simDetailBaseUrl }) => {
  const {
    simDetails,
    error: simDetailsError,
    isLoading: simDetailsIsLoading,
    isValidating: simDetailsIsValidating,
  } = useSimDetails(hologramSimId);

  const {
    simProfiles,
    error: simProfilesError,
    isLoading: simProfilesIsLoading,
    isValidating: simProfilesIsValidating,
  } = useSimProfiles(hologramSimId);

  // TODO: Remove when we have real connected status
  const {
    deviceDetails,
    isLoading: isDeviceDetailsLoading,
    error: deviceDetailsError,
  } = useSimDeviceDetails(hologramSimId);

  const { isSuperAdmin } = useUserInfo();

  const {
    error: openSessionsError,
    isLoading: openSessionsLoading,
    deviceIdsWithOpenSessions,
  } = useOpenSessions(simDetails.organizationId, isSuperAdmin || !!simDetails.organizationId);

  const { networkStatusGroups, isLoading: statusLoading, error: statusError } = useSimStatuses();

  const error =
    simDetailsError || simProfilesError || statusError || openSessionsError || deviceDetailsError;
  const isLoading =
    simDetailsIsLoading ||
    simProfilesIsLoading ||
    statusLoading ||
    openSessionsLoading ||
    isDeviceDetailsLoading;
  const isValidating = simDetailsIsValidating || simProfilesIsValidating;

  // TODO: Respect the release flag
  const fallback = simProfiles?.length > 0 && !!simDetails.fallbackInstalled;
  const fallbackProtected = fallback && !!simDetails.fallbackEnabled;

  const deactivated =
    !!simDetails.networkStatus &&
    !!networkStatusGroups?.deactivated?.includes(simDetails.networkStatus as any);

  const hasMultipleDifferentApns = useMemo(() => {
    let foundMultiple = false;
    let firstApn: string | undefined;

    simProfiles.forEach((simProfile) => {
      // Check to see if we have multiple different required APNs
      if (simProfile.requiredApn && !foundMultiple) {
        if (firstApn) {
          if (firstApn.localeCompare(simProfile.requiredApn) !== 0) {
            foundMultiple = true;
          }
        } else {
          firstApn = simProfile.requiredApn;
        }
      }
    });

    return foundMultiple;
  }, [simProfiles]);

  const { panelContents, footerWell } = useMemo(() => {
    let newPanelContents: ReactNode = null;
    let newFooterWell: ReactNode = null;
    const isDeactivated = networkStatusGroups?.deactivated?.includes(
      simDetails.networkStatus as EnumsSimNetworkState
    );

    if (error) {
      newPanelContents = (
        <PanelNotification
          variant="error"
          label="We ran into an issue loading SIM profiles"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          We will keep trying in the background or you can try to reload the page.
        </PanelNotification>
      );
    } else if (isLoading) {
      newPanelContents = <PanelNotification>Loading...</PanelNotification>;
    } else {
      newPanelContents = simProfiles.map((profile) => (
        <RenderSimProfile
          key={profile.simProfileId}
          simProfile={profile}
          fallback={fallback}
          fallbackProtected={fallbackProtected}
          connected={
            !!(
              deviceDetails.claimedDeviceId &&
              deviceIdsWithOpenSessions.has(deviceDetails.claimedDeviceId) &&
              !networkStatusGroups?.deactivated?.includes(simDetails.networkStatus as any)
            )
          }
          deactivated={isDeactivated}
          renderApnBanner={hasMultipleDifferentApns}
          msisdn={simDetails.msisdn}
          simDetailBaseUrl={simDetailBaseUrl}
          hologramSimId={hologramSimId}
          simHasMultipleProfiles={simProfiles.length > 1}
        />
      ));

      if (!deactivated && fallbackProtected && simProfiles.length >= 2) {
        newFooterWell = (
          <div className={styles.footerWell}>
            <Icon name="Shield" size="medium" />
            <p>
              This SIM is protected from downtime with <strong>Outage Protection</strong>. If your
              SIM loses connection, it will try to reconnect for <strong>15 minutes</strong> before
              switching profiles.
            </p>
          </div>
        );
      }
    }

    return { panelContents: newPanelContents, footerWell: newFooterWell };
  }, [
    networkStatusGroups?.deactivated,
    simDetails.networkStatus,
    simDetails.msisdn,
    error,
    isLoading,
    simProfiles,
    deactivated,
    fallbackProtected,
    fallback,
    deviceDetails.claimedDeviceId,
    deviceIdsWithOpenSessions,
    hasMultipleDifferentApns,
    simDetailBaseUrl,
    hologramSimId,
  ]);

  return (
    <Panel
      header={simProfiles.length === 1 ? 'Profile' : 'Profiles'}
      noContentPadding
      footer={footerWell}
      footerVariant="alternative"
      isLoading={isLoading || isValidating}
    >
      <div className={styles.panel}>{panelContents}</div>
    </Panel>
  );
};

export default SimProfiles;
